import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { projectMembersListInfo, sitesFilterList } from '../../../../../core/projects.selectors';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../state/app.state';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppHttpService, AuthService } from '../../../../../../../shared/services';
import { FetchProjectMembersListRequest } from '../../../../../core/projects.actions';

const MY_TEAM_CREATOR_FILTER = '__my_team__';

@Component({
    selector: 'app-project-details-page-status-site-filter',
    templateUrl: './status-site-filter.component.html',
    styleUrls: ['../styles.scss', './status-site-filter.component.scss'],
    standalone: false
})
export class StatusSiteFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string;
  @Input() isDwrReportingHierarchyEnabled = false;

  isStatusAllChecked = false;
  isSitesAllChecked = false;
  isCreatorsAllChecked = false;
  buttonLabel = 'Filters (0)';
  isOpen = false;
  activeTab: 'status' | 'sites' | 'createdBy' = 'status';
  sitesOptions = [];
  creatorOptions = [];
  projectMembers = [];
  // People removed from the project; their reports still show in the list
  removedMembers = [];
  myTeamAccountIds: string[] = [];

  statusList = [];
  sitesList = [];
  creatorsList = [];

  statusOptions = [
    {
      label: 'Awaiting Submission',
      value: 'draft',
      isChecked: false,
    },
    {
      label: 'Awaiting Approval',
      value: 'submitted-reviewed',
      isChecked: false,
    },
    {
      label: 'Approved',
      value: 'approved',
      isChecked: false,
    },
    {
      label: 'Rejected',
      value: 'rejected',
      isChecked: false,
    },
  ];

  searchForm = new FormGroup({
    statusSearch: new FormControl(''),
    sitesSearch: new FormControl(''),
    creatorsSearch: new FormControl(''),
  });

  private readonly onDestroy: Subject<any> = new Subject<any>();
  private readonly projectIdChanged: Subject<string> = new Subject<string>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private appHttpService: AppHttpService,
  ) {
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.status) {
          if (Array.isArray(qp.status)) {
            this.statusList = qp.status;
          } else {
            this.statusList = [qp.status];
          }

          this.statusOptions.forEach(o => {
            o.isChecked = this.statusList.includes(o.value);
          });

          this.isStatusAllChecked = this.statusList.length === this.statusOptions.length;
        } else {
          this.statusList = [];
        }

        if (qp.site) {
          if (Array.isArray(qp.site)) {
            this.sitesList = qp.site;
          } else {
            this.sitesList = [qp.site];
          }

          this.sitesOptions.forEach(o => {
            o.isChecked = this.sitesList.includes(o.value);
          });

          this.isSitesAllChecked = this.sitesList.length === this.sitesOptions.length;
        } else {
          this.sitesList = [];
        }

        if (qp.creator) {
          if (Array.isArray(qp.creator)) {
            this.creatorsList = qp.creator;
          } else {
            this.creatorsList = [qp.creator];
          }

          this.creatorOptions.forEach(o => {
            o.isChecked = this.creatorsList.includes(o.value);
          });

          this.isCreatorsAllChecked = this.creatorsList.length === this.creatorOptions.length;
        } else {
          this.creatorsList = [];
        }

        this.syncOptionCheckedState();
        this.generateLabel();
      });

    this.store.select(projectMembersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projectMembers = JSON.parse(JSON.stringify(data?.list || []));
        this.updateMyTeamAccountIds();
        this.updateCreatorOptions();
        this.applyDefaultMyTeamFilter();
      });

    this.projectIdChanged
      .pipe(
        filter(projectId => !!projectId),
        switchMap(projectId => this.store.select(sitesFilterList, { projectId })),
        takeUntil(this.onDestroy),
      )
      .subscribe(list => {
        this.sitesOptions = JSON.parse(JSON.stringify(list || []));
        if (!this.showSitesTab && this.activeTab === 'sites') {
          this.activeTab = 'status';
        }
        this.syncOptionCheckedState();
      });

    if (this.projectId) {
      this.projectIdChanged.next(this.projectId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projectId) {
      if (this.projectId) {
        this.store.dispatch(FetchProjectMembersListRequest({
          payload: {
            projectId: this.projectId,
          },
        }));
      }
      this.loadRemovedMembers(this.projectId);

      this.projectIdChanged.next(this.projectId);
    }

    if (changes.isDwrReportingHierarchyEnabled && !changes.isDwrReportingHierarchyEnabled.firstChange) {
      this.updateCreatorOptions();
      this.applyDefaultMyTeamFilter();
    }
  }

  onStatusCheckboxChange(event) {
    this.router.navigate([], {
      queryParams: {
        status: event,
      },
      queryParamsHandling: 'merge',
    });
  }

  onSiteCheckboxChange(event) {
    this.router.navigate([], {
      queryParams: {
        site: event,
      },
      queryParamsHandling: 'merge',
    });
  }

  onCreatorCheckboxChange(event) {
    const selected = this.normalizeCreatorSelection(event);

    this.router.navigate([], {
      queryParams: {
        creator: selected,
      },
      queryParamsHandling: 'merge',
    });
  }

  generateLabel() {
    const statusCount: any = this.statusList?.length ? 1 : 0;
    const sitesCount: any = this.sitesList?.length ? 1 : 0;
    const creatorsCount: any = this.creatorsList?.length ? 1 : 0;

    const totalFilters = statusCount + sitesCount + creatorsCount;

    this.buttonLabel = `Filter (${totalFilters})`;
  }

  get showSitesTab(): boolean {
    return this.sitesOptions.length > 1;
  }

  selectTab(tab: 'status' | 'sites' | 'createdBy'): void {
    this.activeTab = tab;
  }

  private updateCreatorOptions(): void {
    const currentUser: any = this.authService?.getCurrentUser();
    const currentUserId = currentUser?.id || currentUser?.uuid;

    const options = this.projectMembers.map((member: any) => {
      const name = [member?.first_name, member?.last_name].filter(Boolean).join(' ') || member?.email || '';
      return {
        value: member?.uuid || member?.id,
        label: (member?.uuid === currentUserId || member?.id === currentUserId) ? `Me (${name})` : name,
        isChecked: false,
      };
    });

    // Removed members go after current ones so their reports stay filterable
    const listed = new Set(options.map((option: any) => option.value));
    this.removedMembers
      .filter((member: any) => member?.uuid && !listed.has(member.uuid))
      .forEach((member: any) => {
        const name = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email || '';
        options.push({ value: member.uuid, label: `${name} (removed)`, isChecked: false });
      });

    const meIndex = options.findIndex((option: any) => option.value === currentUserId);
    if (meIndex > -1) {
      const [me] = options.splice(meIndex, 1);
      options.unshift(me);
    }

    if (this.shouldShowMyTeamOption()) {
      options.unshift({
        value: MY_TEAM_CREATOR_FILTER,
        label: 'My team',
        isChecked: false,
      });
    }

    this.creatorOptions = options;
    this.syncOptionCheckedState();
  }

  /**
   * Fetch accounts removed from the project (`include=removed` on the members
   * endpoint) for the Created by tab. Kept out of the shared project members
   * store so the Members screen keeps listing current members only.
   */
  private loadRemovedMembers(projectId: string): void {
    this.removedMembers = [];
    if (!projectId) {
      return;
    }

    this.appHttpService
      .getService(`/v1/project/${projectId}/members`, { include: 'removed' })
      .toPromise()
      .then((resp: any) => {
        // Ignore a late response for a project that is no longer shown
        if (this.projectId !== projectId) {
          return;
        }
        this.removedMembers = (resp?.data || []).filter((member: any) => member?.removed);
        this.updateCreatorOptions();
      })
      .catch(() => {
        // Not fatal: Created by still lists current project members
      });
  }

  private updateMyTeamAccountIds(): void {
    const currentUser: any = this.authService?.getCurrentUser();
    const currentUserId = currentUser?.id || currentUser?.uuid;

    if (!currentUserId) {
      this.myTeamAccountIds = [];
      return;
    }

    const reportsByManager = this.projectMembers.reduce((acc: Record<string, any[]>, member: any) => {
      const managerId = member?.reports_to?.uuid || member?.reports_to_account_id;
      if (managerId) {
        acc[managerId] = acc[managerId] || [];
        acc[managerId].push(member);
      }
      return acc;
    }, {});

    const teamIds = new Set<string>([currentUserId]);
    const visit = (accountId: string): void => {
      (reportsByManager[accountId] || []).forEach((member: any) => {
        const memberId = member?.uuid || member?.id;
        if (memberId && !teamIds.has(memberId)) {
          teamIds.add(memberId);
          visit(memberId);
        }
      });
    };

    visit(currentUserId);
    this.myTeamAccountIds = Array.from(teamIds);
  }

  private shouldShowMyTeamOption(): boolean {
    return this.isDwrReportingHierarchyEnabled && this.myTeamAccountIds.length > 1;
  }

  private applyDefaultMyTeamFilter(): void {
    if (
      !this.shouldShowMyTeamOption()
      || this.route.snapshot.queryParamMap.has('creator')
      || this.creatorsList.length
    ) {
      return;
    }

    this.router.navigate([], {
      queryParams: {
        creator: MY_TEAM_CREATOR_FILTER,
      },
      queryParamsHandling: 'merge',
    });
  }

  private normalizeCreatorSelection(selectedOptions: string[]): string[] {
    const selected = selectedOptions || [];
    const hasMyTeam = selected.includes(MY_TEAM_CREATOR_FILTER);
    const hadMyTeam = this.creatorsList.includes(MY_TEAM_CREATOR_FILTER);

    if (hasMyTeam && !hadMyTeam) {
      return [MY_TEAM_CREATOR_FILTER];
    }

    if (hasMyTeam && selected.length > 1) {
      return selected.filter(value => value !== MY_TEAM_CREATOR_FILTER);
    }

    return selected;
  }

  private syncOptionCheckedState(): void {
    this.statusOptions.forEach(o => {
      o.isChecked = this.statusList.includes(o.value);
    });
    this.sitesOptions.forEach(o => {
      o.isChecked = this.sitesList.includes(o.value);
    });
    this.creatorOptions.forEach(o => {
      o.isChecked = this.creatorsList.includes(o.value);
    });

    this.isStatusAllChecked = this.statusList.length === this.statusOptions.length;
    this.isSitesAllChecked = this.sitesList.length === this.sitesOptions.length;
    this.isCreatorsAllChecked = this.creatorsList.length === this.creatorOptions.length;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
